import { useEffect, useState } from "react";
import {
  Alert,
  Backdrop,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Icon,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
} from "@mui/material";
import axios from "axios.js";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import DataTable from "theme/Tables/DataTable";
import MDButton from "theme/components/MDButton";
import MDInput from "theme/components/MDInput";

function ShippingFeesCard({ campaignRef, triggerRefresh, shippingRates, campaignShippingRates }) {
  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [tableRows, setTableRows] = useState([]);
  const [selectedShippingRate, setSelectedShippingRate] = useState(null);
  const [openAttachShippingDialog, setOpenAttachShippingDialog] = useState(false);

  async function removeShipping(ref) {
    setShowLoading(true);
    await axios
      .get(`/portal/campaigns/shipping_rates/${ref}/delete.json`)
      .then((result) => {
        // console.log(result);
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          triggerRefresh();
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  async function attachShippingRate() {
    if (selectedShippingRate === null) {
      setSeverity("error");
      setErrMsg("Select a shipping a collection");
      setOpen(true);
      return;
    }

    setOpenAttachShippingDialog(false);
    setShowLoading(true);
    const json = {
      campaign: campaignRef,
      collection: selectedShippingRate,
    };

    await axios
      .post("portal/campaigns/shipping_rates/attach.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          triggerRefresh();
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  const campaignShippingTableColumns = [
    { Header: "Collection", accessor: "collection", align: "left" },
    { Header: "Added On", accessor: "created", align: "right" },
    { Header: "Actions", accessor: "actions", align: "right" },
  ];

  const buildCampaignShippingRows = (data) => {
    const rows = [];
    data.map((row) => {
      rows.push({
        collection: row.title,
        actions: (
          <MDBox mt={1} variant="button">
            <MDButton
              size="small"
              variant="outlined"
              color="error"
              iconOnly
              onClick={() => {
                if (window.confirm(`Remove collection?`) === true) {
                  removeShipping(row.reference);
                }
              }}
            >
              <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                delete
              </Icon>
            </MDButton>
          </MDBox>
        ),
        created: row.created,
      });
      return null;
    });

    return rows;
  };

  useEffect(() => {
    const cShippingCollections = buildCampaignShippingRows(campaignShippingRates);
    setTableRows(cShippingCollections);
  }, [campaignShippingRates]);

  return (
    <MDBox>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <Dialog open={openAttachShippingDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>Attach Shipping Fees</MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox sx={{ marginTop: "15px" }}>
            <MDBox mt={2}>
              <FormControl sx={{ minWidth: 120, width: "100%" }}>
                <InputLabel>Shipping Fees</InputLabel>
                <Select
                  label="Shipping Fees"
                  onChange={(e) => {
                    setSelectedShippingRate(e.target.value);
                  }}
                  sx={{ padding: "12px", paddingLeft: "3px" }}
                >
                  {
                    // --> Notice below changes
                    shippingRates.length > 0 && // --> Changes from `options.length ? ...` to `options.length > 0 && ...`
                      shippingRates.map((record) => (
                        <MenuItem value={`${record.reference}`}>{record.title}</MenuItem>
                      ))
                  }
                </Select>
              </FormControl>
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenAttachShippingDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="dark"
            variant="contained"
            onClick={() => {
              attachShippingRate();
            }}
          >
            Attach
          </MDButton>
        </DialogActions>
      </Dialog>
      <MDBox mt={3}>
        <Card sx={{ height: "100%", marginTop: "0px" }}>
          <MDBox>
            <MDBox p={2}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h6" fontWeight="light">
                  <b>Shipping Fees</b>
                  <br />
                  <small>Need to collect shipping fees? Attach a shipping fee collection.</small>
                </MDTypography>
                <MDButton
                  variant="contained"
                  color="dark"
                  iconOnly
                  onClick={() => {
                    setSelectedShippingRate(null);
                    setOpenAttachShippingDialog(true);
                  }}
                >
                  <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                </MDButton>
              </MDBox>
            </MDBox>
            <MDBox>
              <DataTable
                table={{ columns: campaignShippingTableColumns, rows: tableRows }}
                isSorted
                entriesPerPage={false}
                showTotalEntries
                buttons
                noEndBorder
              />
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>
    </MDBox>
  );
}

export default ShippingFeesCard;
